/**
 * Zentraler API-Client — Fetch-Wrapper mit Bearer-Injection + 401-Refresh-Retry.
 * Abgeleitet aus BuildScan (src/core/auth/api-client.ts).
 *
 * API_BASE ist '/api/v1' (Single-Origin): in Dev via Vite-Proxy, in Produktion
 * via Caddy — dieselbe Domain, daher kein CORS.
 */

import { useAuthStore } from './auth.store';
import { meldeServerOk, meldeServerFehler } from '@core/composables/useOnline';

export const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const auth = useAuthStore();

  const doFetch = async (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    try {
      const antwort = await fetch(`${API_BASE}${path}`, { ...init, headers });
      meldeServerOk();               // Server hat geantwortet (auch bei 4xx/5xx)
      return antwort;
    } catch (e) {
      meldeServerFehler();           // Netz-/Verbindungsfehler
      throw e;
    }
  };

  let response = await doFetch(auth.accessToken);

  if (response.status === 401) {
    await auth.refresh();
    if (!auth.accessToken) {
      throw new ApiError('Sitzung abgelaufen — bitte neu einloggen', 401);
    }
    response = await doFetch(auth.accessToken);
    if (response.status === 401) {
      auth.logout();
      throw new ApiError('Sitzung abgelaufen — bitte neu einloggen', 401);
    }
  }

  return response;
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await apiFetch(path, { ...init, headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = typeof body.detail === 'string' ? body.detail : `Fehler ${response.status}`;
    throw new ApiError(detail, response.status);
  }
  // 204 (No Content) hat keinen Body.
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
